/**
 * korvibe — 국가별 통화 "표시" 변환기 (display-only)
 *
 * 동작: 방문자 IP로 통화를 감지해, 페이지의 USD 가격을 그 나라 통화로 환산해 보여준다.
 * 한계: 실제 결제(checkout)는 스토어 기본 통화(USD)로 진행된다. 표시만 바꾼다.
 *       → Shopify Payments 미사용 스토어는 결제 통화를 바꿀 수 없기 때문(플랫폼 제약).
 *
 * 검증 단계용 구현. 지오/환율은 무료 공개 API + localStorage 캐시 사용.
 * 트래픽이 커지면 무료 API rate limit에 걸리므로, 그때 백엔드로 옮긴다.
 */
(function () {
  'use strict';

  // ===== 설정 (자유롭게 커스텀) =====
  var CONFIG = {
    // 스토어 기본/결제 통화. Shopify 전역값이 있으면 그걸 우선 사용.
    baseCurrency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
    // 가격이 들어있는 요소들 (Horizon 테마 기준)
    // 카트 페이지 가격들은 .price 를 안 쓰므로 별도 leaf 선택자를 추가한다.
    // (선택자는 해당 페이지에만 존재 → 다른 페이지에선 매칭 0개라 무해)
    priceSelectors: [
      '.price', '.compare-at-price',
      '.cart-items__unit-price-wrapper span', // 라인 단가
      '.cart-items__price text-component',    // 라인 합계
      '.cart-totals__total-value',            // 예상 합계
      '.cart__total-original-price',          // 합계 정가(취소선)
      '.cart-totals__original-value',         // 소계 (코드 할인 시)
      '.cart-discounts__value',               // 할인액 (코드 할인 시)
      '.cart-discount-summary .value',        // 하단 커스텀 합계(Original/Discount/After)
    ],
    // 결제는 USD라는 안내 배지 표시 여부
    showNotice: true,
    // {from}=감지 통화, {to}=결제 통화로 치환됨
    noticeText: '✅ Shown in {from} for reference — you\'ll be charged in {to} at checkout.',
    // 캐시 유효 시간(시간)
    cacheHours: 12,
  };

  var CACHE_KEY = 'kv_currency_v3';
  // 가격 토큰만 매칭 ($1,234.56 / $19.99 / $10). .price 요소 안에서만 쓰므로 안전.
  var MONEY_RE = /\$[\d,]+(?:\.\d+)?/g;

  // 브라우저 시간대(IANA) → 통화코드. 네트워크 호출 없이 OS 정보로 판단 → 애드블로커 무관.
  // 여기 없는 Europe/* 는 아래에서 EUR로 처리. 그 외 미지정 시간대는 변환 안 함(USD 유지).
  var TZ_CURRENCY = {
    // 유럽 비유로존 (Europe/* 기본 EUR의 예외)
    'Europe/London': 'GBP', 'Europe/Zurich': 'CHF', 'Europe/Stockholm': 'SEK',
    'Europe/Oslo': 'NOK', 'Europe/Copenhagen': 'DKK', 'Europe/Warsaw': 'PLN',
    'Europe/Prague': 'CZK', 'Europe/Budapest': 'HUF', 'Europe/Bucharest': 'RON',
    'Europe/Sofia': 'BGN', 'Europe/Moscow': 'RUB', 'Europe/Istanbul': 'TRY',
    'Europe/Kyiv': 'UAH', 'Europe/Kiev': 'UAH', 'Europe/Reykjavik': 'ISK',
    'Atlantic/Reykjavik': 'ISK',
    // 아메리카
    'America/New_York': 'USD', 'America/Chicago': 'USD', 'America/Denver': 'USD',
    'America/Los_Angeles': 'USD', 'America/Phoenix': 'USD', 'America/Anchorage': 'USD',
    'Pacific/Honolulu': 'USD',
    'America/Toronto': 'CAD', 'America/Vancouver': 'CAD', 'America/Edmonton': 'CAD',
    'America/Winnipeg': 'CAD', 'America/Halifax': 'CAD',
    'America/Mexico_City': 'MXN', 'America/Sao_Paulo': 'BRL',
    'America/Argentina/Buenos_Aires': 'ARS', 'America/Santiago': 'CLP',
    'America/Bogota': 'COP', 'America/Lima': 'PEN',
    // 아시아·중동
    'Asia/Seoul': 'KRW', 'Asia/Tokyo': 'JPY', 'Asia/Shanghai': 'CNY',
    'Asia/Urumqi': 'CNY', 'Asia/Hong_Kong': 'HKD', 'Asia/Taipei': 'TWD',
    'Asia/Singapore': 'SGD', 'Asia/Kuala_Lumpur': 'MYR', 'Asia/Bangkok': 'THB',
    'Asia/Jakarta': 'IDR', 'Asia/Manila': 'PHP', 'Asia/Ho_Chi_Minh': 'VND',
    'Asia/Kolkata': 'INR', 'Asia/Karachi': 'PKR', 'Asia/Dhaka': 'BDT',
    'Asia/Dubai': 'AED', 'Asia/Riyadh': 'SAR', 'Asia/Jerusalem': 'ILS',
    'Asia/Qatar': 'QAR', 'Asia/Kuwait': 'KWD',
    // 오세아니아·아프리카
    'Australia/Sydney': 'AUD', 'Australia/Melbourne': 'AUD', 'Australia/Brisbane': 'AUD',
    'Australia/Perth': 'AUD', 'Pacific/Auckland': 'NZD',
    'Africa/Johannesburg': 'ZAR', 'Africa/Lagos': 'NGN', 'Africa/Cairo': 'EGP',
  };

  // 시간대 → 통화. 미지정 Europe/* 는 EUR로 간주. 없으면 null(변환 안 함).
  function detectCurrency() {
    var tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) {}
    if (TZ_CURRENCY[tz]) return TZ_CURRENCY[tz];
    if (tz.indexOf('Europe/') === 0) return 'EUR';
    return null;
  }

  function readCache() {
    try {
      var c = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (c && Date.now() - c.t < CONFIG.cacheHours * 3600e3) return c;
    } catch (e) {}
    return null;
  }
  function writeCache(c) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch (e) {}
  }

  // 방문자 통화(시간대 기반) + USD 기준 환율을 가져온다. 변환 불필요/미지정이면 null.
  function resolve() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached.skip ? null : cached);

    var cur = detectCurrency();
    if (!cur || cur === CONFIG.baseCurrency) {
      writeCache({ t: Date.now(), skip: true });
      return Promise.resolve(null);
    }
    return fetch('https://open.er-api.com/v6/latest/' + CONFIG.baseCurrency)
      .then(function (r) { return r.json(); })
      .then(function (fx) {
        var rate = fx && fx.rates && fx.rates[cur];
        if (!rate) return null;
        var ctx = { t: Date.now(), cur: cur, rate: rate };
        writeCache(ctx);
        return ctx;
      })
      .catch(function () { return null; });
  }

  function makeFormatter(cur) {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: cur });
    } catch (e) {
      return { format: function (v) { return v.toFixed(2) + ' ' + cur; } };
    }
  }

  function convertMoney(text, ctx) {
    MONEY_RE.lastIndex = 0;
    return text.replace(MONEY_RE, function (tok) {
      var usd = parseFloat(tok.replace(/[^0-9.]/g, ''));
      return isNaN(usd) ? tok : ctx.fmt.format(usd * ctx.rate);
    });
  }

  // el.textContent = out 으로 통째로 덮으면 자식 요소가 전부 지워진다.
  // Horizon 가격 마크업은 <span class="visually-hidden">Sale price</span><span class="money">$47.31</span>
  // 구조라, 덮어쓰면 숨김 라벨이 평문이 되어 "Sale price ₩64,187"처럼 화면에 노출된다.
  // 그래서 텍스트 노드만 순회하며 금액 토큰을 치환해 요소 구조를 보존한다.
  function replaceMoneyInTextNodes(el, ctx) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var nodes = [], n;
    while ((n = walker.nextNode())) nodes.push(n);
    nodes.forEach(function (node) {
      var next = convertMoney(node.nodeValue, ctx);
      if (next !== node.nodeValue) node.nodeValue = next;
    });
  }

  function convertEl(el, ctx) {
    // 원본 USD 텍스트를 저장해두고, 매 변환은 원본 기준으로 다시 계산한다.
    // 카트는 수량 변경 시 서버가 가격을 다시 그린다. 그때 현재 텍스트가 우리가 마지막에
    // 쓴 값(data-kv-done)과 다르면 = 밑단 USD가 새 값으로 갱신된 것이므로 원본을 다시 캐시한다.
    // (이게 없으면 옛 가격 기준으로 환산돼 금액이 어긋남)
    var orig = el.getAttribute('data-kv-orig');
    if (orig != null && el.textContent !== el.getAttribute('data-kv-done')) orig = null;
    if (orig == null) {
      orig = el.textContent;
      MONEY_RE.lastIndex = 0;
      if (!MONEY_RE.test(orig)) return; // 가격 토큰 없으면 패스
      el.setAttribute('data-kv-orig', orig);
    }
    var out = convertMoney(orig, ctx);
    if (el.textContent !== out) replaceMoneyInTextNodes(el, ctx); // 동일하면 안 써서 무한 루프 방지
    el.setAttribute('data-kv-done', out);             // 우리가 쓴 값 기록(다음 틱 비교용)
  }

  function convertAll(ctx) {
    CONFIG.priceSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) { convertEl(el, ctx); });
    });
  }

  // 안내문을 1회 삽입. 상세페이지(/products/)=메인 가격 블록 안, 카트=예상 합계 바로 아래.
  function maybeInjectNotice(ctx) {
    if (!CONFIG.showNotice) return;
    if (document.getElementById('kv-currency-notice')) return; // 페이지당 1회(중복 방지)

    var isProduct = location.pathname.indexOf('/products/') !== -1;
    var isCart = /\/cart\/?$/.test(location.pathname);
    // 상세: <product-price> 안쪽(beforeend) — 부모 flex의 gap을 피하려 안쪽에 넣음.
    // 카트: 예상 합계(.cart-totals__total) 바로 아래(afterend) — 합계 박스 안.
    var host = isProduct ? document.querySelector('product-price')
             : isCart    ? document.querySelector('.cart-totals__total')
             : null;
    if (!host) return;

    var note = document.createElement('div');
    note.id = 'kv-currency-notice';
    note.textContent = CONFIG.noticeText.replace('{from}', ctx.cur).replace('{to}', CONFIG.baseCurrency);
    // 카트 합계 박스는 어두운 색상 스킴이라 스킴 기반 subdued 색을 써서 대비 확보.
    var color = isCart ? 'rgb(var(--color-foreground-rgb))' : '#000000ff';
    // 카트는 옆의 "Taxes and shipping..."(.cart-totals__tax-note small)와 동일하게:
    //   크기 = --font-size--2xs, 줄 간격 = 그 안내처럼 미지정(박스 기본값 상속).
    // 상세페이지는 기존 그대로(h5 크기 + line-height 1.1) — 건드리지 않음.
    var sizeLine = isCart ? 'font-size:var(--font-size--xs);'
                          : 'font-size:var(--font-h5--size);line-height:1.1;';
    // 상세는 부모 gap이 없어 가격에 너무 붙으므로 8px로 띄움. 카트는 부모 row-gap이 있어 3px 유지.
    var marginTop = isProduct ? '8px' : '3px';
    note.style.cssText = 'margin-top:' + marginTop + ';' + sizeLine +
      'font-family:var(--font-paragraph--family);font-weight:var(--font-paragraph--weight);' +
      'color:' + color + ';text-wrap:wrap;'; // text-wrap:wrap = 이 문구만 균형 해제, 끝까지 채워 줄바꿈
    host.insertAdjacentElement(isProduct ? 'beforeend' : 'afterend', note);
  }

  function render(ctx) {
    convertAll(ctx);
    maybeInjectNotice(ctx);
  }

  function start(ctx) {
    ctx.fmt = makeFormatter(ctx.cur);
    render(ctx);

    // Horizon은 variant 변경/장바구니 갱신/페이지 전환 시 다시 렌더하므로 재적용.
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; render(ctx); });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function init() {
    resolve().then(function (ctx) { if (ctx) start(ctx); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();